import React, { useState } from 'react';
import { Plus, Trash2, Pencil, Copy, Archive, ArchiveRestore, ArrowUp, ArrowDown, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const uid = () => Math.random().toString(36).slice(2, 10);

const getPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
const setPath = (obj, path, value) => {
  const next = JSON.parse(JSON.stringify(obj));
  const keys = path.split('.');
  let cur = next;
  for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]] = cur[keys[i]] ?? {};
  cur[keys[keys.length - 1]] = value;
  return next;
};

const FieldInput = ({ field, value, onChange, context }) => {
  const t = field.type;
  if (t === 'textarea') {
    return <Textarea rows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={`pe-field-${field.key}`} />;
  }
  if (t === 'bool') {
    return (
      <div className="flex items-center gap-2 h-9">
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`pe-field-${field.key}`} />
        <span className="text-xs text-stone-500">{value ? 'Yes' : 'No'}</span>
      </div>
    );
  }
  if (t === 'select') {
    return (
      <select
        value={value ?? (field.options?.[0]?.[0] || '')}
        onChange={e => onChange(e.target.value)}
        className="w-full h-9 rounded-md border border-stone-200 bg-white px-3 text-sm"
        data-testid={`pe-field-${field.key}`}
      >
        {(field.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  }
  if (t === 'tags') {
    return (
      <Input
        value={Array.isArray(value) ? value.join(', ') : (value ?? '')}
        placeholder={field.placeholder}
        onChange={e => onChange(e.target.value.split(',').map(s => s.trimStart()))}
        onBlur={e => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
        data-testid={`pe-field-${field.key}`}
      />
    );
  }
  if (t === 'multiselect') {
    const options = (context?.[field.optionsKey] || []).map(o => [o.id, o.name]);
    if (field.allowAll) options.unshift(['all', `All ${field.optionsKey}`]);
    const arr = Array.isArray(value) ? value : [];
    const toggle = (id) => onChange(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`pe-field-${field.key}`}>
        {options.length === 0 && <span className="text-xs text-stone-400">No items configured yet</span>}
        {options.map(([id, name]) => (
          <button
            key={id} type="button" onClick={() => toggle(id)}
            data-testid={`pe-multiselect-${field.key}-${id}`}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${arr.includes(id) ? 'bg-[#1A3626] text-white border-[#1A3626]' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'}`}
          >
            {name}
          </button>
        ))}
      </div>
    );
  }
  if (t === 'objlist') {
    const rows = Array.isArray(value) ? value : [];
    const updateRow = (i, k, v) => {
      const next = rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r);
      onChange(next);
    };
    return (
      <div className="space-y-2" data-testid={`pe-field-${field.key}`}>
        {rows.map((row, i) => (
          <div key={row.id || i} className="flex items-end gap-2">
            {field.columns.map(col => (
              <div key={col.key} className="flex-1 min-w-0">
                <label className="text-[10px] text-stone-400 block">{col.label}</label>
                <Input
                  type={col.type === 'number' ? 'number' : col.type === 'time' ? 'time' : 'text'}
                  value={row[col.key] ?? ''}
                  onChange={e => updateRow(i, col.key, col.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
                  className="h-8 text-xs"
                  data-testid={`pe-objlist-${field.key}-${i}-${col.key}`}
                />
              </div>
            ))}
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} data-testid={`pe-objlist-remove-${field.key}-${i}`}>
              <X className="w-3.5 h-3.5 text-stone-400" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onChange([...rows, { id: uid() }])} data-testid={`pe-objlist-add-${field.key}`}>
          <Plus className="w-3 h-3 mr-1" /> Add row
        </Button>
      </div>
    );
  }
  return (
    <Input
      type={t === 'number' ? 'number' : t === 'time' ? 'time' : t === 'date' ? 'date' : 'text'}
      step={field.step}
      value={value ?? ''}
      placeholder={field.placeholder}
      onChange={e => onChange(t === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
      data-testid={`pe-field-${field.key}`}
    />
  );
};

export const ItemListEditor = ({ items = [], onChange, schema, context, testId }) => {
  const [editing, setEditing] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);

  const openNew = () => {
    const blank = { id: uid(), enabled: true, archived: false };
    setEditing(blank);
    setEditIndex(-1);
  };
  const openEdit = (item, index) => {
    setEditing(JSON.parse(JSON.stringify(item)));
    setEditIndex(index);
  };
  const saveItem = () => {
    const name = getPath(editing, schema.nameField);
    if (!name) return;
    const next = [...items];
    if (editIndex === -1) next.push(editing);
    else next[editIndex] = editing;
    onChange(next);
    setEditing(null);
  };
  const removeItem = (i) => {
    if (!window.confirm('Delete this item?')) return;
    onChange(items.filter((_, idx) => idx !== i));
  };
  const duplicateItem = (i) => {
    const copy = JSON.parse(JSON.stringify(items[i]));
    copy.id = uid();
    copy[schema.nameField] = `${copy[schema.nameField] || ''} (Copy)`;
    const next = [...items];
    next.splice(i + 1, 0, copy);
    onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const patch = (i, changes) => onChange(items.map((it, idx) => idx === i ? { ...it, ...changes } : it));

  return (
    <div data-testid={testId || `pe-list-${schema.nameField}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-medium text-stone-900">{schema.title}</h3>
          {schema.description && <p className="text-xs text-stone-500 mt-0.5">{schema.description}</p>}
        </div>
        <Button size="sm" onClick={openNew} className="bg-[#1A3626] hover:bg-[#254834] shrink-0" data-testid="pe-add-item-btn">
          <Plus className="w-4 h-4 mr-1" /> Add {schema.itemLabel}
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-stone-400 border border-dashed border-stone-200 rounded-lg py-8 text-center">
          No {schema.itemLabel.toLowerCase()}s yet. Click &quot;Add {schema.itemLabel}&quot; to create one.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => (
            <div
              key={item.id || i}
              className={`bg-white border rounded-lg px-4 py-3 flex items-center gap-3 ${item.archived ? 'opacity-50 border-dashed border-stone-300' : 'border-stone-200'}`}
              data-testid={`pe-item-${i}`}
            >
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(i, -1)} className="text-stone-300 hover:text-stone-600" disabled={i === 0} data-testid={`pe-item-move-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => move(i, 1)} className="text-stone-300 hover:text-stone-600" disabled={i === items.length - 1} data-testid={`pe-item-move-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">
                  {getPath(item, schema.nameField) || 'Untitled'}
                  {item.archived && <span className="ml-2 text-[10px] uppercase text-stone-400">Archived</span>}
                </p>
                {schema.summary && <p className="text-xs text-stone-500 truncate">{schema.summary(item)}</p>}
              </div>
              <Switch checked={item.enabled !== false} onCheckedChange={v => patch(i, { enabled: v })} data-testid={`pe-item-toggle-${i}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item, i)} data-testid={`pe-item-edit-${i}`}>
                <Pencil className="w-4 h-4 text-stone-500" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicateItem(i)} title="Duplicate" data-testid={`pe-item-duplicate-${i}`}>
                <Copy className="w-4 h-4 text-stone-400" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => patch(i, { archived: !item.archived })} title={item.archived ? 'Restore' : 'Archive'} data-testid={`pe-item-archive-${i}`}>
                {item.archived ? <ArchiveRestore className="w-4 h-4 text-stone-400" /> : <Archive className="w-4 h-4 text-stone-400" />}
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(i)} data-testid={`pe-item-delete-${i}`}>
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={v => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editIndex === -1 ? `New ${schema.itemLabel}` : `Edit ${schema.itemLabel}`}</DialogTitle>
            <DialogDescription>{schema.title}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              {schema.fields.map(f => (
                <div key={f.key} className={['textarea', 'objlist', 'tags', 'multiselect'].includes(f.type) ? 'sm:col-span-2' : ''}>
                  <label className="text-xs font-medium text-stone-500 mb-1 block">{f.label}</label>
                  <FieldInput
                    field={f}
                    value={getPath(editing, f.key)}
                    onChange={v => setEditing(prev => setPath(prev, f.key, v))}
                    context={context}
                  />
                </div>
              ))}
              <div className="sm:col-span-2 pt-2">
                <Button onClick={saveItem} className="w-full bg-[#1A3626] hover:bg-[#254834]" data-testid="pe-item-save-btn">
                  Save {schema.itemLabel}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
